import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { MarketplaceItem, MarketplaceCoupon, marketplaceCategories } from "@shared/schema";
import { normalizeImageUrl } from "@/lib/image-url";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  Star,
  Copy,
  Check,
  ExternalLink,
  Ticket
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { PageContainer } from "@/components/layout/PageContainer";

const regions = [
  { value: "ALL", label: "전체 지역" },
  { value: "NSW", label: "뉴사우스웨일스" },
  { value: "VIC", label: "빅토리아" },
  { value: "QLD", label: "퀸즐랜드" },
  { value: "SA", label: "남호주" },
  { value: "WA", label: "서호주" },
  { value: "TAS", label: "태즈매니아" },
  { value: "NT", label: "노던 테리토리" },
  { value: "ACT", label: "수도 준주" },
];

export default function MarketplaceDetail() {
  const [match, params] = useRoute<{ id: string }>("/marketplace/:id");
  const { user, membershipLevel } = useAuth();
  const { toast } = useToast();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [viewTracked, setViewTracked] = useState(false);

  // Check if params is valid
  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">유효하지 않은 상품 ID입니다</p>
            <Link href="/marketplace">
              <Button variant="outline">목록으로</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const itemId = params.id;

  // Fetch item details
  const { data: item, isLoading } = useQuery<MarketplaceItem>({
    queryKey: ["/api/marketplace/items", itemId],
    queryFn: async () => {
      const response = await fetch(`/api/marketplace/items/${itemId}`);
      if (!response.ok) throw new Error("Failed to fetch item");
      return response.json();
    },
    enabled: !!itemId,
  });

  // Fetch available coupons for this item
  const { data: coupons = [] } = useQuery<MarketplaceCoupon[]>({
    queryKey: ["/api/marketplace/coupons"],
    queryFn: async () => {
      const response = await fetch("/api/marketplace/coupons");
      if (!response.ok) throw new Error("Failed to fetch coupons");
      return response.json();
    },
  });

  // Track view
  const trackViewMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/marketplace/items/${itemId}/view`, {}),
  });

  // Track click
  const trackClickMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/marketplace/items/${itemId}/click`, {}),
  });

  // Track view on mount
  useEffect(() => {
    if (itemId && !viewTracked) {
      trackViewMutation.mutate();
      setViewTracked(true);
    }
  }, [itemId]);

  // Filter coupons for this business and check membership
  const availableCoupons = coupons.filter((coupon) => {
    if (coupon.status !== "active") return false;
    if (coupon.businessName !== item?.businessName) return false;

    // Check membership requirement
    if (coupon.membershipRequired === "platinum" && membershipLevel !== "platinum") {
      return false;
    }
    if (coupon.membershipRequired === "premium" &&
        membershipLevel !== "premium" && membershipLevel !== "platinum") {
      return false;
    }

    // Check date validity
    const now = new Date();
    const start = new Date(coupon.startDate);
    const end = new Date(coupon.endDate);
    if (now < start || now > end) return false;

    // Check usage limit
    if (coupon.usageLimit &&
        coupon.usedCount &&
        coupon.usedCount >= coupon.usageLimit) {
      return false;
    }

    return true;
  });

  const getCategoryLabel = (categoryId: string) => {
    return marketplaceCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  const getDiscountPercentage = () => {
    if (!item?.originalPrice || !item?.salePrice) return null;
    const original = Number(item.originalPrice);
    const sale = Number(item.salePrice);
    if (original <= sale) return null;
    return Math.round(((original - sale) / original) * 100);
  };

  const handleCopyCoupon = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast({
      title: "복사 완료",
      description: `쿠폰 코드 "${code}"가 복사되었습니다.`,
    });
  };

  const handleExternalClick = (url: string) => {
    trackClickMutation.mutate();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">로딩 중...</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">상품을 찾을 수 없습니다</p>
        <Link href="/marketplace">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로 돌아가기
          </Button>
        </Link>
      </div>
    );
  }

  const discount = getDiscountPercentage();

  return (
    <PageContainer size="default">
        <Link href="/marketplace">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <div className="relative bg-muted rounded-lg">
            <img
              src={normalizeImageUrl(item.imageUrl) || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'}
              alt={item.titleKo || item.title}
              className="w-full rounded-lg shadow-lg object-cover"
              style={{ maxHeight: "500px" }}
              onError={(e) => {
                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
            {item.featured && (
              <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                <Star className="h-4 w-4 mr-1 fill-current" />
                추천
              </Badge>
            )}
            {discount && (
              <Badge className="absolute top-4 right-4 bg-destructive text-destructive-foreground text-lg px-4 py-2">
                {discount}% OFF
              </Badge>
            )}
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            <div>
              <p className="text-muted-foreground mb-2">{item.businessName}</p>
              <h1 className="text-3xl font-bold mb-4">
                {item.titleKo || item.title}
              </h1>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Badge variant="outline">
                  <Tag className="h-3 w-3 mr-1" />
                  {getCategoryLabel(item.category)}
                </Badge>
                <Badge variant="outline">
                  <MapPin className="h-3 w-3 mr-1" />
                  {regions.find(r => r.value === item.region)?.label}
                </Badge>
                {(item.isPremiumOnly || item.isPlatinumOnly) && (
                  <Badge variant="secondary">
                    {item.isPlatinumOnly ? "플래티넘 전용" : "프리미엄 전용"}
                  </Badge>
                )}
              </div>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-4xl font-bold text-primary">
                  ${Number(item.salePrice).toFixed(2)}
                </span>
                {item.originalPrice && Number(item.originalPrice) > Number(item.salePrice) && (
                  <span className="text-xl text-muted-foreground line-through">
                    ${Number(item.originalPrice).toFixed(2)}
                  </span>
                )}
              </div>

              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {item.descriptionKo || item.description}
              </p>
            </div>

            <Separator />

            {/* Contact Information */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">연락처</h3>
              <div className="space-y-2">
                {item.contactPhone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${item.contactPhone}`} className="hover:text-foreground">
                      {item.contactPhone}
                    </a>
                  </div>
                )}
                {item.contactEmail && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${item.contactEmail}`} className="hover:text-foreground">
                      {item.contactEmail}
                    </a>
                  </div>
                )}
                {item.website && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="h-4 w-4" />
                    <button
                      onClick={() => handleExternalClick(item.website!)}
                      className="hover:text-foreground hover:underline"
                      data-testid="link-website"
                    >
                      {item.website}
                    </button>
                  </div>
                )}
                {item.address && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 mt-1" />
                    <div>
                      <p>{item.address}</p>
                      {item.city && item.state && (
                        <p>{item.city}, {item.state} {item.postcode}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {item.website && (
              <>
                <Separator />
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => handleExternalClick(item.website!)}
                  data-testid="button-visit-website"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  웹사이트 방문하기
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Available Coupons */}
        {availableCoupons.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Ticket className="h-6 w-6" />
              사용 가능한 쿠폰
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableCoupons.map((coupon) => (
                <Card key={coupon.id} data-testid={`card-coupon-${coupon.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {coupon.titleKo || coupon.title}
                        </CardTitle>
                        {coupon.featured && (
                          <Badge variant="secondary" className="mt-2">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            추천
                          </Badge>
                        )}
                      </div>
                      <Badge className="text-lg px-3 py-1">
                        {coupon.discountType === "percentage"
                          ? `${Number(coupon.discountValue)}%`
                          : `$${Number(coupon.discountValue)}`
                        }
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {coupon.description && (
                      <p className="text-sm text-muted-foreground">
                        {coupon.descriptionKo || coupon.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <Input
                        value={coupon.code}
                        readOnly
                        className="font-mono font-semibold text-center"
                        data-testid={`input-coupon-${coupon.id}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleCopyCoupon(coupon.code)}
                        data-testid={`button-copy-${coupon.id}`}
                      >
                        {copiedCode === coupon.code ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>유효기간: ~ {coupon.endDate}</span>
                      {coupon.membershipRequired !== "free" && (
                        <Badge variant="outline" className="text-xs">
                          {coupon.membershipRequired === "platinum" ? "플래티넘" : "프리미엄"}
                        </Badge>
                      )}
                    </div>

                    {coupon.minimumPurchase && (
                      <p className="text-xs text-muted-foreground">
                        최소 구매액: ${Number(coupon.minimumPurchase).toFixed(2)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No access message for premium/platinum items */}
        {!user && (item.isPremiumOnly || item.isPlatinumOnly) && (
          <Card className="mt-8 border-primary/50">
            <CardContent className="py-6">
              <p className="text-center text-muted-foreground">
                이 상품은 {item.isPlatinumOnly ? "플래티넘" : "프리미엄"} 회원 전용입니다.
              </p>
              <div className="flex justify-center mt-4">
                <Link href="/mypage">
                  <Button variant="default">
                    회원가입하기
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
    </PageContainer>
  );
}
